/**
 * Hub data + helpers extracted from AdminLayout.jsx during the iter175
 * layout refactor. This is the single source of truth for the admin
 * sidebar navigation, the global "+ New" creator menu, and the search
 * type-to-icon mapping. Adding a new hub or content type here makes it
 * automatically appear in the sidebar, flyout popups, search index,
 * and "+ New" dropdown.
 */
import {
  LayoutDashboard, FileText, Briefcase, Users, Shield, ScrollText,
  Mail, BarChart3, Eye, Globe, Search, Inbox, Star, BookOpen,
  Target, Building2, Layers, Database, Handshake, Workflow,
  TrendingUp, Activity, FolderKanban, UserCog, Footprints, Download,
  Megaphone, UserSquare, UserMinus, FileSignature, CalendarDays,
  DollarSign, User, ClipboardCheck,
} from 'lucide-react';
import {
  MarketingHubIcon, SalesHubIcon, RecruitmentHubIcon, ContentHubIcon,
  AdminHubIcon, HRHubIcon,
} from '../HubIcons';

export const TOP_ITEMS = [
  { label: 'Dashboard', icon: LayoutDashboard, href: '/admin', testId: 'dashboard', permission: 'dashboard' },
];

export const HUBS = [
  {
    id: 'marketing-hub', label: 'Marketing Hub', hubIcon: MarketingHubIcon,
    hubColor: '#F5841F', hubColorBg: '#F5841F18',
    permission: 'analytics',
    groups: [
      {
        label: 'Performance',
        items: [
          { label: 'Analytics', icon: BarChart3, href: '/admin/analytics', testId: 'analytics', permission: 'analytics' },
          { label: 'Content Analytics', icon: Eye, href: '/admin/content-analytics', testId: 'content-analytics', permission: 'analytics' },
          { label: 'CTA Performance', icon: Target, href: '/admin/cta-performance', testId: 'cta-performance', permission: 'analytics' },
        ],
      },
      {
        label: 'Search',
        items: [
          { label: 'SEO Analytics', icon: Search, href: '/admin/seo', testId: 'seo-analytics', permission: 'analytics' },
          { label: 'Search Console', icon: Globe, href: '/admin/search-console', testId: 'search-console', permission: 'analytics' },
        ],
      },
    ],
  },
  {
    id: 'sales-hub', label: 'Sales Hub', hubIcon: SalesHubIcon,
    hubColor: '#2D7FF9', hubColorBg: '#2D7FF918',
    permission: 'crm',
    items: [
      { label: 'CRM Dashboard', icon: Handshake, href: '/admin/crm-dashboard', testId: 'crm-dashboard', permission: 'hubspot' },
      { label: 'HubSpot CRM', icon: Handshake, href: '/admin/hubspot', testId: 'hubspot-crm', permission: 'hubspot' },
      { label: 'Lead Scores', icon: TrendingUp, href: '/admin/leads', testId: 'lead-scores', permission: 'leads' },
      { label: 'Lead Journey', icon: Footprints, href: '/admin/lead-journey', testId: 'lead-journey', permission: 'leads' },
      { label: 'Submissions', icon: Inbox, href: '/admin/submissions', testId: 'submissions', permission: 'submissions' },
      { label: 'CRM Digest', icon: Mail, href: '/admin/digest', testId: 'crm-digest', permission: 'digest' },
      { label: 'Revenue', icon: DollarSign, href: '/admin/card-authorizations', testId: 'card-authorizations', permission: 'hubspot' },
    ],
  },
  {
    id: 'recruitment-hub', label: 'Recruitment Hub', hubIcon: RecruitmentHubIcon,
    hubColor: '#10B981', hubColorBg: '#10B98118',
    permission: 'jobs',
    items: [
      { label: 'Job Listings', icon: Briefcase, href: '/admin/jobs', testId: 'job-listings', badge: 'jobs', permission: 'jobs' },
      { label: 'Applications', icon: Inbox, href: '/admin/applications', testId: 'applications', badge: 'apps', permission: 'applications' },
    ],
  },
  {
    id: 'content-hub', label: 'Content Hub', hubIcon: ContentHubIcon,
    hubColor: '#A855F7', hubColorBg: '#A855F718',
    permission: 'blog',
    items: [
      { label: 'Blog Posts', icon: FileText, href: '/admin/blog', testId: 'blog-posts', badge: 'blog', permission: 'blog' },
      { label: 'Content Pages', icon: Layers, href: '/admin/content-pages', testId: 'content-pages', permission: 'blog_write' },
      { label: 'Case Studies', icon: BookOpen, href: '/admin/case-studies', testId: 'case-studies-admin', permission: 'blog_write' },
      { label: 'About Pages', icon: Building2, href: '/admin/about-pages', testId: 'about-pages-admin', permission: 'blog_write' },
      { label: 'Content Calendar', icon: CalendarDays, href: '/admin/content-calendar', testId: 'content-calendar', permission: 'blog' },
      { label: 'Media Library', icon: FolderKanban, href: '/admin/media', testId: 'media-library', permission: 'blog' },
    ],
  },
  {
    id: 'hr-hub', label: 'HR Hub', hubIcon: HRHubIcon,
    hubColor: '#EC4899', hubColorBg: '#EC489918',
    permission: 'dashboard',
    groups: [
      {
        label: 'People Operations',
        items: [
          { label: 'Employees', icon: Users, href: '/admin/hrms/employees', testId: 'hrms-employees', permission: 'dashboard' },
          { label: 'Attendance', icon: Activity, href: '/admin/hrms/attendance', testId: 'hrms-attendance', permission: 'dashboard' },
          { label: 'Leave Requests', icon: ClipboardCheck, href: '/admin/hrms/leave', testId: 'hrms-leave', permission: 'dashboard' },
          { label: 'Payroll', icon: DollarSign, href: '/admin/hrms/payroll', testId: 'hrms-payroll', permission: 'dashboard' },
          { label: 'Performance Reviews', icon: Star, href: '/admin/hrms/reviews', testId: 'hrms-reviews', permission: 'dashboard' },
          { label: 'Holiday Calendar', icon: CalendarDays, href: '/admin/hrms/holidays', testId: 'hrms-holidays', permission: 'dashboard' },
        ],
      },
      {
        label: 'Employee Lifecycle',
        items: [
          { label: 'Onboarding', icon: Workflow, href: '/admin/hrms/onboarding', testId: 'hrms-onboarding', permission: 'dashboard' },
          { label: 'Offer Letters', icon: FileSignature, href: '/admin/hrms/offers', testId: 'hrms-offers', permission: 'dashboard' },
          { label: 'Offboarding', icon: UserMinus, href: '/admin/hrms/offboarding', testId: 'hrms-offboarding', permission: 'dashboard' },
        ],
      },
      {
        label: 'Workplace',
        items: [
          { label: 'Announcements', icon: Megaphone, href: '/admin/announcements', testId: 'announcements', permission: 'dashboard' },
          { label: 'HR Contacts', icon: UserSquare, href: '/admin/hr-contacts', testId: 'hr-contacts', permission: 'dashboard' },
          { label: 'Drive', icon: FolderKanban, href: '/admin/drive', testId: 'flynaut-drive', permission: 'dashboard' },
        ],
      },
    ],
  },
  {
    id: 'admin-hub', label: 'Admin Hub', hubIcon: AdminHubIcon,
    hubColor: '#64748B', hubColorBg: '#64748B18',
    permission: 'security',
    groups: [
      {
        label: 'Security & Governance',
        items: [
          { label: 'Security', icon: Shield, href: '/admin/security', testId: 'security', permission: 'security' },
          { label: 'Compliance', icon: ClipboardCheck, href: '/admin/compliance', testId: 'compliance', permission: 'compliance' },
          { label: 'Audit Log', icon: ScrollText, href: '/admin/audit', testId: 'audit-log', permission: 'audit' },
          { label: 'Login Activity', icon: Activity, href: '/admin/login-activity', testId: 'login-activity', permission: 'security' },
        ],
      },
      {
        label: 'Access Control',
        items: [
          { label: 'User Management', icon: Users, href: '/admin/users', testId: 'user-management', permission: 'users' },
          { label: 'Roles & Access', icon: UserCog, href: '/admin/roles', testId: 'roles-access', permission: 'users' },
        ],
      },
      {
        label: 'Configuration',
        items: [
          { label: 'Email Templates', icon: Mail, href: '/admin/email-settings', testId: 'email-settings', permission: 'email_settings' },
          { label: 'Report Recipients', icon: Mail, href: '/admin/report-recipients', testId: 'report-recipients', permission: 'security' },
          { label: 'Download History', icon: Download, href: '/admin/download-history', testId: 'download-history', permission: 'analytics' },
          { label: 'Telemetry', icon: Activity, href: '/admin/telemetry', testId: 'telemetry', permission: 'analytics' },
        ],
      },
      {
        label: 'System',
        items: [
          { label: 'MCP Server', icon: Database, href: '/admin/mcp', testId: 'mcp-server', permission: 'security' },
        ],
      },
    ],
  },
];

// Helper: flatten a hub's items whether they are grouped or flat.
export function getHubItems(hub) {
  if (hub.items) return hub.items;
  if (hub.groups) return hub.groups.flatMap((g) => g.items);
  return [];
}

// Global "+ New" creator menu, kept in one place so it grows alongside
// content types without polluting the sidebar.
export const NEW_ACTIONS = [
  { label: 'New blog post', href: '/admin/blog/new', icon: FileText, testId: 'new-blog-post', permission: 'blog_write' },
  { label: 'New job posting', href: '/admin/jobs/new', icon: Briefcase, testId: 'new-job', permission: 'jobs_write' },
  { label: 'New case study', href: '/admin/case-studies', icon: BookOpen, testId: 'new-case-study', permission: 'blog_write' },
  { label: 'New announcement', href: '/admin/announcements?new=1', icon: Megaphone, testId: 'new-announcement', permission: 'dashboard' },
];

export const SEARCH_TYPE_ICONS = {
  blog: FileText, case_study: BookOpen, service: Layers,
  application: Inbox, employee: User, job: Briefcase, nav: LayoutDashboard,
};
